import React, { useMemo, useState } from 'react';
import { Form, Formik, FormikHelpers, FormikProps, FormikState } from 'formik';
import { IconColor, SalvButton, SalvLabel, SalvModal, SalvTooltipWrapper, Size } from '@salvapp/ui-components';
import * as Yup from 'yup';

import {
  InternalApiReviewRequest,
  InternalApiReviewRequestEntityType,
  InternalApiReviewStatus,
  InternalApiScenario,
  InternalApiScenarioEntityType,
  InternalApiScenarioIntervalTimeUnit,
  InternalApiScenarioLanguage,
  InternalApiScenarioStatus,
  InternalApiScenarioTypeV2,
  InternalApiScenarioVersionCommand,
  InternalApiScenarioVersionParameter,
  InternalApiScenarioVersionParameterType,
  InternalApiScenarioVersionStatus,
} from '../../../api-internal';
import { DATE_TIME_FORMAT, dateTimeFormatRegex, testInput } from '../../../common/helper';
import ScenarioVersionRightPanel from './ScenarioVersionRightPanel';
import ScenarioVersionLeftPanel from './ScenarioVersionLeftPanel';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { scenarioVersionInternalApi } from '../../../apis';
import './ScenarioVersionModal.scss';
import {
  SCENARIO_VERSION_QUERY_KEY,
  useCreateScenarioVersion,
  useUpdateScenarioVersion,
} from '../scenarioVersionActions';
import dayjs from 'dayjs';
import { getScenarioVersionStatus, scenarioVersionStatusColorVariant } from '../../../common/versioning/helper';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import { useMe } from '../../../organisation/useMe';
import { CAN_MODIFY_SCENARIO } from '../../../auth/permissions';
import {
  invalidateReviewRequestQueries,
  useResolveReviewRequest,
  useReviewRequest,
} from '../../../review-requests/reviewRequestActions';
import { SCENARIO_QUERY_KEY } from '../../scenarioQueryActions';

export interface ScenarioVersionModalFormValues {
  status: InternalApiScenarioVersionStatus;
  details: string;
  includeRelatedTransactions: boolean;
  query: string;
  note: string;
  language: InternalApiScenarioLanguage;
  suppressionInterval: number;
  periodicInterval?: number;
  periodicIntervalTimeUnit?: InternalApiScenarioIntervalTimeUnit;
  startTime?: string;
  parameters: InternalApiScenarioVersionParameter[];
  logic: string;
  segments: string[];
  excludedSegments: string[];
}

export interface ScenarioVersionModalProps {
  scenario: InternalApiScenario;
  versionId: string | null;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  disabled?: boolean;
}

export enum QueryValidationStatus {
  NOT_STARTED = 'NOT STARTED',
  IN_PROGRESS = 'IN PROGRESS',
  FAILED = 'FAILED',
  PASSED = 'PASSED',
}

const SECONDS_IN_DAY = 86400;
export const parameterSchema = Yup.object().shape({
  name: Yup.string().required('Parameter name is required'),
  displayName: Yup.string().required('Parameter display name is required'),
  value: Yup.lazy((value) =>
    typeof value === 'number' ? Yup.number().required('Parameter value is required') : Yup.array().of(Yup.string())
  ),
  type: Yup.string().required('Parameter type is required'),
  timeUnit: Yup.string().when('type', {
    is: InternalApiScenarioVersionParameterType.PERIOD,
    then: (schema) => schema.required('Parameter time unit is required'),
    otherwise: (schema) => schema,
  }),
});

export const ScenarioVersionModal = ({
  scenario,
  versionId,
  showModal,
  setShowModal,
  disabled = false,
}: ScenarioVersionModalProps) => {
  const { currentOrganisation, permissions, id: userId } = useMe();
  const dispatch = useDispatch();
  const [queryValidationStatus, setQueryValidationStatus] = useState<QueryValidationStatus>(
    QueryValidationStatus.NOT_STARTED
  );

  const { data: version } = useQuery({
    queryKey: [SCENARIO_VERSION_QUERY_KEY, versionId, currentOrganisation?.id],
    queryFn: async () =>
      await scenarioVersionInternalApi.getScenarioVersion({ scenarioId: scenario.id, id: versionId! }),
    enabled: !!versionId,
  });

  const queryClient = useQueryClient();
  const { mutate: createScenarioVersion } = useCreateScenarioVersion();
  const { mutate: updateScenarioVersion } = useUpdateScenarioVersion(() => {
    invalidateReviewRequestQueries(
      queryClient,
      currentOrganisation?.id,
      versionId!,
      InternalApiReviewRequestEntityType.SCENARIO_VERSION
    );
  });

  const isPeriodic = scenario.type === InternalApiScenarioTypeV2.PERIODIC;
  const isPeriodicEntity = scenario.type === InternalApiScenarioTypeV2.PERIODIC_ENTITY;
  const isDraft = !version || version.status === InternalApiScenarioVersionStatus.DRAFT;

  const { data: reviewRequest } = useReviewRequest(InternalApiReviewRequestEntityType.SCENARIO_VERSION, versionId);
  const { mutate: resolveReviewRequest } = useResolveReviewRequest(undefined, () => {
    queryClient.invalidateQueries({ queryKey: [SCENARIO_VERSION_QUERY_KEY, versionId, currentOrganisation?.id] });
    queryClient.invalidateQueries({ queryKey: [SCENARIO_QUERY_KEY, scenario.id, currentOrganisation?.id] });
  });

  const canEditVersion = isDraft && permissions.includes(CAN_MODIFY_SCENARIO) && !disabled;

  const periodicScenarioAttributes = {
    startTime: version?.periodicScenarioAttributes?.startTime
      ? dayjs(version.periodicScenarioAttributes.startTime).format(DATE_TIME_FORMAT)
      : dayjs().format(DATE_TIME_FORMAT),
    periodicIntervalTimeUnit:
      version?.periodicScenarioAttributes?.periodicIntervalTimeUnit || InternalApiScenarioIntervalTimeUnit.WEEK,
    periodicInterval: version?.periodicScenarioAttributes?.periodicInterval || 1,
  };

  const initialValues: ScenarioVersionModalFormValues = useMemo(
    () => ({
      details: version?.details || '',
      includeRelatedTransactions: version?.includeRelatedTransactions || false,
      query: version?.query || '',
      note: version?.note || '',
      status: version?.status || InternalApiScenarioVersionStatus.DRAFT,
      language: version?.language || InternalApiScenarioLanguage.SQL,
      suppressionInterval: version?.suppressionInterval ? Math.floor(version.suppressionInterval / SECONDS_IN_DAY) : 0,
      ...((isPeriodic || isPeriodicEntity) && periodicScenarioAttributes),
      parameters: version?.parameters || [],
      logic: version?.logic || '',
      segments: version?.segments || [],
      excludedSegments: version?.excludedSegments || [],
    }),
    [version]
  );

  const initialFormikState: Partial<FormikState<ScenarioVersionModalFormValues>> = {
    values: initialValues,
    isSubmitting: false,
    isValidating: false,
  };

  const validationSchema = Yup.object().shape(
    {
      details: Yup.string().required('Details are required'),
      note: Yup.string().required('Note is required'),
      query: Yup.string().required('Query is required'),
      suppressionInterval: Yup.number().min(0).required('Suppression interval is required'),
      ...((isPeriodic || isPeriodicEntity) && {
        periodicInterval: Yup.number().positive().required('Periodic interval is required'),
        periodicIntervalTimeUnit: Yup.string().required('Interval time unit is required'),
        startTime: Yup.string()
          .required('Start time is required')
          .matches(dateTimeFormatRegex, DATE_TIME_FORMAT)
          .test('Cannot contain HTML', 'Cannot contain HTML', (value) => testInput(value)),
      }),
      ...(!isPeriodic && {
        segments: Yup.array().of(Yup.string()),
      }),
      parameters: Yup.array().of(parameterSchema),
      logic: Yup.string().required('Version logic is required'),
    },
    []
  );

  const submitVersion = async (
    formValues: ScenarioVersionModalFormValues,
    { setSubmitting }: FormikHelpers<ScenarioVersionModalFormValues>
  ) => {
    const internalApiScenarioVersionCommand: InternalApiScenarioVersionCommand = {
      status: formValues.status,
      details: formValues.details,
      includeRelatedTransactions: formValues.includeRelatedTransactions,
      query: formValues.query,
      note: formValues.note,
      language: formValues.language,
      suppressionInterval: formValues.suppressionInterval! * SECONDS_IN_DAY,
      parameters: formValues.parameters,
      ...((isPeriodic || isPeriodicEntity) && {
        periodicScenarioAttributes: {
          startTime: dayjs(formValues.startTime, DATE_TIME_FORMAT).toDate() as Date,
          periodicInterval: formValues.periodicInterval!,
          periodicIntervalTimeUnit: formValues.periodicIntervalTimeUnit!,
        },
      }),
      logic: formValues.logic,
      segments: formValues.segments,
      excludedSegments: formValues.excludedSegments,
    };

    if (!versionId) {
      createScenarioVersion({
        scenarioId: scenario.id,
        internalApiScenarioVersionCommand,
      });
    } else {
      updateScenarioVersion({
        scenarioId: scenario.id,
        id: versionId,
        internalApiScenarioVersionCommand,
      });
    }

    setSubmitting(false);
    setShowModal(false);
  };

  const getTitle = () => {
    const internalIdOrEmpty = scenario.internalId ? `${scenario.internalId} ` : '';
    if (!version) {
      return 'Add scenario ' + internalIdOrEmpty + 'version';
    }

    if (canEditVersion) {
      return 'Edit scenario ' + internalIdOrEmpty + `version ${version.versionId}`;
    } else {
      return 'View scenario ' + internalIdOrEmpty + `version ${version.versionId}`;
    }
  };

  const getActions = (
    handle: string | null,
    reviewRequest: InternalApiReviewRequest | null | undefined,
    submitCurrentVersion: (e: any) => void,
    handleClose: () => void
  ) => {
    const inReview = !!reviewRequest;
    const isAuthor = reviewRequest?.createdBy.id === userId;
    if (handle && inReview && !isAuthor) {
      return (
        <>
          <SalvButton
            variant='primary'
            label='Approve'
            size={Size.LARGE}
            onClick={() =>
              dispatch(
                showConfirmationModal({
                  title: 'Are you sure you want to make this version current?',
                  iconColor: IconColor.DANGER,
                  description:
                    scenario.status === InternalApiScenarioStatus.ACTIVE
                      ? 'The new settings will be applied for scenario execution after this.'
                      : 'Setting this version to current will not activate the scenario. In order to make it live, activate the scenario separately.',
                  confirmationText: 'Yes',
                  cancelText: 'No',
                  onConfirm: () => {
                    resolveReviewRequest({
                      entityId: handle,
                      entityType: InternalApiReviewRequestEntityType.SCENARIO_VERSION,
                      status: InternalApiReviewStatus.APPROVED,
                    });
                    handleClose();
                  },
                })
              )
            }
          />
          <SalvButton
            variant='danger'
            label='Reject'
            size={Size.LARGE}
            onClick={() =>
              dispatch(
                showConfirmationModal({
                  title: 'Are you sure you want to reject this change?',
                  iconColor: IconColor.DANGER,
                  description:
                    scenario.status === InternalApiScenarioStatus.ACTIVE
                      ? 'This version will be saved as a draft. Scenario execution will not be affected.'
                      : 'This version will be saved as a draft.',
                  confirmationText: 'Yes',
                  cancelText: 'No',
                  onConfirm: () => {
                    resolveReviewRequest({
                      entityId: handle,
                      entityType: InternalApiReviewRequestEntityType.SCENARIO_VERSION,
                      status: InternalApiReviewStatus.REJECTED,
                    });
                    handleClose();
                  },
                })
              )
            }
          />
        </>
      );
    } else if (handle && inReview && isAuthor) {
      return (
        <SalvButton
          variant='danger'
          label='Withdraw'
          size={Size.LARGE}
          onClick={() =>
            dispatch(
              showConfirmationModal({
                title: 'Are you sure you want to withdraw this change?',
                iconColor: IconColor.DANGER,
                description:
                  scenario.status === InternalApiScenarioStatus.ACTIVE
                    ? 'This version will be saved as a draft. Scenario execution will not be affected.'
                    : 'This version will be saved as a draft.',
                confirmationText: 'Yes',
                cancelText: 'No',
                onConfirm: () => {
                  resolveReviewRequest({
                    entityId: handle,
                    entityType: InternalApiReviewRequestEntityType.SCENARIO_VERSION,
                    status: InternalApiReviewStatus.WITHDRAWN,
                  });
                  handleClose();
                },
              })
            )
          }
        />
      );
    } else {
      return (
        <>
          <SalvButton variant='muted' label='Save as draft' size={Size.LARGE} type='submit' />
          <SalvTooltipWrapper
            tooltip={'You need to validate the scenario first'}
            isDisabled={queryValidationStatus === QueryValidationStatus.PASSED}
          >
            <SalvButton
              disabled={queryValidationStatus !== QueryValidationStatus.PASSED}
              variant='primary'
              label='Make version current'
              size={Size.LARGE}
              onClick={(e) =>
                dispatch(
                  showConfirmationModal({
                    title: 'Are you sure you want to make this version current?',
                    iconColor: IconColor.DANGER,
                    description:
                      scenario.status === InternalApiScenarioStatus.ACTIVE
                        ? 'The new settings will be applied for scenario execution after this'
                        : 'Setting this version to current will not activate the scenario. In order to make it live, activate the scenario separately',
                    confirmationText: 'Yes',
                    cancelText: 'No',
                    onConfirm: () => submitCurrentVersion(e),
                  })
                )
              }
            />
          </SalvTooltipWrapper>
        </>
      );
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitVersion}
      validationSchema={validationSchema}
      validateOnChange={false}
      enableReinitialize={true}
    >
      {({ resetForm, setFieldValue, handleSubmit, isSubmitting }: FormikProps<ScenarioVersionModalFormValues>) => {
        const handleClose = () => {
          resetForm(initialFormikState);
          setShowModal(false);
        };

        const submitCurrentVersion = (e: any) => {
          setFieldValue('status', InternalApiScenarioVersionStatus.LIVE).then(() => handleSubmit(e));
        };

        const isNewVersion = !versionId;
        return (
          <SalvModal
            show={showModal && (isNewVersion || !!version)}
            onHide={handleClose}
            className='scenario-version-modal'
            allowDismissal={false}
          >
            <Form>
              <SalvModal.Header>{getTitle()}</SalvModal.Header>
              <SalvModal.Body>
                <div className='scenario-version-modal-container'>
                  <div className='left-panel'>
                    {version && (
                      <div className='d-flex p-b-24'>
                        <div className='text-strong-s m-r-8'>Version status</div>
                        <SalvLabel variant={scenarioVersionStatusColorVariant(version.status)}>
                          {getScenarioVersionStatus(version.status, !!reviewRequest)}
                        </SalvLabel>
                      </div>
                    )}

                    <ScenarioVersionLeftPanel
                      isDraft={isDraft}
                      isDisabled={isSubmitting || !canEditVersion || !!reviewRequest}
                      isPeriodic={isPeriodic}
                      isPeriodicEntity={isPeriodicEntity}
                      isTransactionScenario={scenario.entityType === InternalApiScenarioEntityType.TRANSACTION}
                    />
                  </div>

                  <ScenarioVersionRightPanel
                    scenarioId={scenario.id}
                    isDisabled={isSubmitting || !canEditVersion || !!reviewRequest}
                    isLibraryScenario={false}
                    isPeriodic={isPeriodic || isPeriodicEntity}
                    queryValidationStatus={queryValidationStatus}
                    setQueryValidationStatus={setQueryValidationStatus}
                    entityType={scenario.entityType}
                  />
                </div>
              </SalvModal.Body>
              {canEditVersion && (
                <SalvModal.Footer>
                  {getActions(versionId, reviewRequest, submitCurrentVersion, handleClose)}
                </SalvModal.Footer>
              )}
            </Form>
          </SalvModal>
        );
      }}
    </Formik>
  );
};

export default ScenarioVersionModal;
